import { clamp, GameId } from '@common'
import { StoreMission } from '@common/models/bonus'
import cx from 'classnames'
import Image, { getImageProps } from 'next/image'
import { useTranslations } from 'next-intl'

import BsCheckMark from '@/assets/images/storefronts/brawlstars/bonus-item-checkmark.png'
import BrawlFlare from '@/assets/images/storefronts/brawlstars/rays.png'
import CocCheckMark from '@/assets/images/storefronts/clashofclans/bonus-item-checkmark.png'
import { getBackgroundImage } from '@/features/storefronts/_storeBase/assetRendering/imageUtils'
import { PointsTag } from '@/features/storefronts/_storeBase/card/v2/shared/Tags/components/PointsTag'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { useGameSlug } from '@/features/storefronts/game-provider'

import { useGetSupportedMissions } from './useGetSupportedMissions'
import { ProgressBar } from '../../shared/ProgressBar'

import styles from './bonusMissionItem.module.scss'

const getCheckMarkImage = (gameSlug: GameId) => {
  if (gameSlug === 'brawlstars') {
    return BsCheckMark
  }

  if (gameSlug === 'squadbusters') {
    return BsCheckMark
  }

  return CocCheckMark
}

interface BonusMissionProps {
  mission: StoreMission
}

export const BonusMissionItem = ({ mission }: BonusMissionProps) => {
  const t = useTranslations()
  const gameSlug = useGameSlug()

  const supportedMissions = useGetSupportedMissions({ gameId: gameSlug, mission })

  if (!mission) {
    return null
  }

  const { current, rewardPoints, target } = mission

  if (!supportedMissions) {
    return null
  }

  const mappedMissionData = supportedMissions[mission?.missionId]

  const { title, type, image } = mappedMissionData
  const isBinary = type === 'binary'
  const isComplete = current >= target
  const progressText = `${clamp(current, 0, target)}/${target}`
  const progressPercentage = target ? (100 * current) / target : 0

  const {
    props: { srcSet },
  } = getImageProps({ alt: '', width: 400, height: 400, src: BrawlFlare })
  const backgroundImage = getBackgroundImage(srcSet)

  return (
    <div
      className={cx(styles.item, styles[gameSlug], {
        [styles.completed]: isComplete,
      })}
    >
      <div className={styles.content}>
        {isComplete && (
          <div className={styles.completedIconContainer}>
            <Image src={getCheckMarkImage(gameSlug)} alt={t('mission_completed')} width={40} height={40} />
          </div>
        )}
        <div className={styles.detailsContainer}>
          <div className={styles.textGroup}>
            {isComplete && (
              <StoreTypography
                className={styles.compltetedText}
                tag="span"
                textSize="sm"
                style="plain"
                overrideFont="supercellText"
              >
                {t('mission_completed')}!
              </StoreTypography>
            )}
            <StoreTypography
              className={styles.title}
              tag="span"
              textSize="md"
              overrideFont="supercellHeadline"
              style="plain"
            >
              {title}
            </StoreTypography>

            {!isComplete && (
              <StoreTypography
                className={styles.progressText}
                tag="span"
                textSize="sm"
                style="plain"
                overrideFont="supercellText"
                lineHeight="tight"
              >
                {isBinary ? t('bonus_track_incomplete') : progressText}
              </StoreTypography>
            )}
          </div>

          <div className={styles.progressBarContainer}>
            <ProgressBar progressPercentage={progressPercentage} completed={isComplete} />
          </div>
        </div>

        <div className={styles.missionItemContainer}>
          <div className={styles.imageContainer}>
            <div className={styles.flareContainer} style={{ backgroundImage: backgroundImage }} />

            <div className={styles.missionItemImage}>
              <div className={styles.imageWrapper}>
                <Image
                  className={styles.image}
                  src={image}
                  alt="reward-image"
                  height={100}
                  width={100}
                  quality={100}
                  priority
                />
              </div>
            </div>
          </div>

          <div className={cx(styles.pointsContainer, { [styles.completed]: isComplete })}>
            <PointsTag
              points={rewardPoints}
              variant={isComplete ? 'BonusDark' : 'Bonus'}
              tagIcon={isComplete ? 'Points' : 'Mission'}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

BonusMissionItem.displayName = 'BonusMissionItem'
